/**
 * @file QuestCeQueLeCrossfit.tsx
 * @summary Article de blog — QuestCeQueLeCrossfit
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, CalendarCheck, MessageSquare, Dumbbell, Users, Heart, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";

const URL = "https://crossfitchistera.fr/blog/quest-ce-que-le-crossfit";
const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const RESAWOD = "https://social.resawod.com/fr/box/crossfit-chistera/reserver";

const faq = [
  {
    "@type": "Question",
    name: "Peut-on commencer le CrossFit sans être sportif ?",
    acceptedAnswer: { "@type": "Answer", text: "Oui. Les séances sont adaptées à tous les niveaux. Un débutant ne fait pas les mêmes charges ni les mêmes mouvements qu'un compétiteur — l'objectif est de progresser à son rythme, sous l'œil d'un coach." },
  },
  {
    "@type": "Question",
    name: "Le CrossFit est-il réservé aux athlètes ?",
    acceptedAnswer: { "@type": "Answer", text: "Non. La majorité des adhérents de CrossFit Chistera sont des pratiquants loisirs : cadres actifs, parents, anciens sportifs, personnes qui reprennent. Les athlètes confirmés sont une minorité visible, pas la norme." },
  },
  {
    "@type": "Question",
    name: "Faut-il être jeune pour commencer ?",
    acceptedAnswer: { "@type": "Answer", text: "Non. Le CrossFit s'adapte à l'âge et aux capacités physiques. Charges, amplitudes, sauts, course et récupération sont ajustés. Beaucoup d'adhérents commencent après 40, 50 ou 60 ans." },
  },
  {
    "@type": "Question",
    name: "Le CrossFit remplace-t-il un autre sport ?",
    acceptedAnswer: { "@type": "Answer", text: "Pas forcément. Beaucoup de pratiquants le combinent avec la course, le rugby, le vélo, le trail, les sports de combat ou le padel. Le CrossFit sert souvent de préparation physique générale en complément." },
  },
  {
    "@type": "Question",
    name: "Le CrossFit fait-il perdre du poids ?",
    acceptedAnswer: { "@type": "Answer", text: "Combiné à une bonne hygiène de vie, oui : amélioration du cardio, de la masse musculaire, du tonus, de l'énergie au quotidien. L'objectif principal reste de se sentir mieux physiquement, et les résultats esthétiques suivent naturellement." },
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "C'est quoi le CrossFit ? Guide débutant complet",
    description: "Découvrez ce qu'est réellement le CrossFit, pourquoi les coachs et adhérents de CrossFit Chistera l'ont choisi et à qui cette méthode s'adresse.",
    author: { "@type": "Organization", name: "CrossFit Chistera" },
    publisher: { "@type": "Organization", name: "CrossFit Chistera", logo: { "@type": "ImageObject", url: "https://crossfitchistera.fr/og-image-square.png?v=4" } },
    image: "https://crossfitchistera.fr/og-image-square.png?v=4",
    datePublished: "2026-01-15",
    dateModified: "2026-05-15",
    mainEntityOfPage: URL,
    inLanguage: "fr-FR",
  },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

const profils = [
  { icon: Users, title: "Débutants", desc: "Premier pas dans une vraie salle, accompagnement personnalisé." },
  { icon: Dumbbell, title: "Anciens sportifs", desc: "Reprendre une pratique structurée après une coupure." },
  { icon: Heart, title: "Parents", desc: "Une heure pour soi, encadrée, efficace, sans logistique." },
  { icon: ShieldCheck, title: "Seniors", desc: "Force, mobilité, équilibre — adapté à l'âge et au corps." },
  { icon: Sparkles, title: "Personnes qui reprennent", desc: "Après plusieurs années sans activité, sans jugement." },
];

const disciplines = [
  "Renforcement musculaire",
  "Cardio",
  "Gymnastique",
  "Mobilité",
  "Endurance",
  "Coordination",
];

const equipements = [
  "Rameur",
  "Haltères",
  "Poids du corps",
  "Kettlebells",
  "Corde à sauter",
  "Tractions",
  "Mobilité",
];

function CTAButtons({ variant = "default" }: { variant?: "default" | "hero" }) {
  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${variant === "hero" ? "justify-start" : "justify-center"}`}>
      <Button asChild size="lg" className="bg-cta text-cta-foreground hover:bg-cta/90 font-body normal-case">
        <a href={RESAWOD} target="_blank" rel="noopener noreferrer">
          <CalendarCheck className="w-4 h-4" /> Réserver une séance d'essai
        </a>
      </Button>
      <Button asChild size="lg" variant="outline" className="border-gold text-gold hover:bg-gold hover:text-gold-foreground font-body normal-case">
        <Link to="/#contact">
          <MessageSquare className="w-4 h-4" /> Parler à un coach
        </Link>
      </Button>
    </div>
  );
}

export default function QuestCeQueLeCrossfit() {
  return (
    <>
      <SEO
        title="C'est quoi le CrossFit ? Guide débutant complet | Chistera"
        description="Découvrez ce qu'est réellement le CrossFit, pourquoi les coachs et adhérents de CrossFit Chistera l'ont choisi et à qui cette méthode s'adresse."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            {/* Breadcrumb */}
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground">
                <ArrowLeft className="w-3 h-3" /> Accueil
              </Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-muted-foreground">Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">C'est quoi le CrossFit ?</span>
            </nav>

            {/* Hero */}
            <header className="mb-10">
              <span className="inline-block text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">Guide débutant · Lecture ~ 7 min</span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">Le CrossFit : ce sport qu'on croit connaître… avant de l'essayer</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                Quand on entend le mot <em>« CrossFit »</em>, les images qui viennent en tête sont souvent les mêmes : des athlètes très musclés, des entraînements extrêmes, des pneus géants à retourner. Pourtant, quand les nouveaux adhérents arrivent chez <strong>CrossFit Chistera</strong>, beaucoup découvrent quelque chose de très différent. Un sport intense, oui — mais surtout adaptable, progressif et étonnamment accessible.
              </p>
              <CTAButtons variant="hero" />
            </header>

            {/* Réponse courte */}
            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mb-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-2">En une phrase</p>
              <p className="text-foreground/90 leading-relaxed">
                Le CrossFit est une méthode d'entraînement qui combine renforcement musculaire, cardio, gymnastique et mobilité, dans des séances courtes, variées et coachées, pour développer une condition physique <strong>utile dans la vie quotidienne</strong> — à tout âge et tout niveau.
              </p>
            </aside>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>Le CrossFit, concrètement, c'est quoi ?</h2>
              <p>
                Le CrossFit est une méthode d'entraînement qui mélange plusieurs disciplines :
              </p>
              <ul className="not-prose grid sm:grid-cols-2 gap-2 my-4">
                {disciplines.map((d) => (
                  <li key={d} className="flex items-center gap-2 text-foreground/90 font-body normal-case">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {d}
                  </li>
                ))}
              </ul>
              <p>
                L'idée est simple : développer une condition physique globale utile dans la vie quotidienne. Porter, courir, monter des escaliers, bouger plus facilement, être plus en forme. Les séances changent chaque jour — c'est justement ce qui plaît à beaucoup d'adhérents : on ne tombe jamais dans la routine.
              </p>

              <h2>Pourquoi les coachs de CrossFit Chistera ont choisi cette méthode</h2>
              <p>
                Les coachs de Chistera parlent souvent d'un même constat : beaucoup de personnes arrêtent le sport parce qu'elles s'ennuient. Toujours les mêmes machines, toujours les mêmes séances, peu de suivi.
              </p>
              <p>Le CrossFit permet au contraire :</p>
              <ul>
                <li>un accompagnement personnalisé</li>
                <li>une progression visible</li>
                <li>des entraînements variés</li>
                <li>une vraie dynamique collective</li>
              </ul>
              <p>Mais surtout, il rend le sport plus vivant. Un des coachs résume souvent la philosophie de la box ainsi :</p>
              <blockquote className="border-l-4 border-gold pl-4 italic text-foreground/90">
                « On veut que les gens ressortent fatigués… mais contents d'être venus. »
              </blockquote>

              <h2>« Je pensais ne jamais aimer une salle de sport »</h2>
              <p>C'est une phrase qu'on entend régulièrement chez Chistera. Julie, 34 ans, raconte :</p>
              <blockquote className="border-l-4 border-gold pl-4 italic text-foreground/90">
                « Avant, je prenais des abonnements en salle que je finissais par abandonner. Ici, je viens parce que j'aime l'ambiance. »
              </blockquote>
              <p>Mathieu, ancien joueur de rugby, explique lui aussi ce qui l'a surpris :</p>
              <blockquote className="border-l-4 border-gold pl-4 italic text-foreground/90">
                « Je pensais retrouver un esprit compétitif permanent. En réalité, les gens s'encouragent énormément. »
              </blockquote>
              <p>Chez CrossFit Chistera, les profils sont très variés et chacun adapte les exercices à son niveau.</p>
            </div>

            {/* Profils */}
            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">À qui s'adresse le CrossFit chez Chistera ?</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {profils.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div key={p.title} className="border border-border bg-card rounded-lg p-5 hover:border-gold/50 transition-colors">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="font-heading text-lg text-foreground">{p.title}</h3>
                      </div>
                      <p className="text-foreground/80 text-sm font-body normal-case">{p.desc}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

            {/* CTA mid */}
            <div className="my-16 border border-gold/30 bg-charcoal/40 rounded-lg px-6 py-8 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Curieux d'essayer ?</h2>
              <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">
                2 séances d'essai gratuites pour comprendre, en vrai, ce qu'est le CrossFit chez Chistera.
              </p>
              <CTAButtons />
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85">

              <h2>Est-ce qu'on soulève vraiment des pneus ?</h2>
              <p>
                Oui… parfois. Mais beaucoup moins souvent que ce qu'internet laisse imaginer. Le CrossFit utilise une grande variété d'exercices :
              </p>
              <ul className="not-prose grid sm:grid-cols-2 gap-2 my-4">
                {equipements.map((e) => (
                  <li key={e} className="flex items-center gap-2 text-foreground/90 font-body normal-case">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {e}
                  </li>
                ))}
              </ul>
              <p>Et occasionnellement des pneus. Mais personne ne passe ses journées à pousser des tracteurs dans un hangar sombre.</p>

              <h2>Est-ce que le CrossFit fait perdre du poids ?</h2>
              <p>
                Le CrossFit combine travail cardiovasculaire et renforcement musculaire. Avec une pratique régulière et une hygiène de vie cohérente, beaucoup d'adhérents constatent :
              </p>
              <ul>
                <li>une amélioration de leur condition physique</li>
                <li>une perte de masse grasse</li>
                <li>plus d'énergie au quotidien</li>
                <li>un meilleur tonus musculaire</li>
              </ul>
              <p>
                Mais chez Chistera, le discours reste volontairement simple. L'objectif principal n'est pas seulement esthétique. Le but est surtout de <strong>se sentir mieux physiquement</strong>. Et souvent, les résultats suivent naturellement.
              </p>

              <h2>Peut-on pratiquer un autre sport à côté ?</h2>
              <p>
                Oui, et c'est même très fréquent. De nombreux adhérents pratiquent aussi la course à pied, le trail, le rugby, les sports de combat, le vélo ou le padel. Le CrossFit est souvent utilisé comme préparation physique générale : cardio, force, mobilité, récupération, explosivité. C'est aussi pour cette raison que beaucoup de sportifs de l'<strong>Est lyonnais</strong> et du Nord-Isère l'intègrent dans leur routine.
              </p>

              <h2>Une box affiliée CrossFit officiellement reconnue</h2>
              <p>
                CrossFit Chistera est officiellement affiliée CrossFit et référencée sur le site international crossfit.com.
              </p>
              <p className="not-prose">
                <a href="https://www.crossfit.com/gym/31856/CrossFit%20Chistera" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                  → Voir la page officielle sur crossfit.com
                </a>
              </p>
              <p>Cette affiliation garantit notamment :</p>
              <ul>
                <li>le respect de la méthodologie CrossFit</li>
                <li>une structure officiellement reconnue</li>
                <li>un encadrement formé (CrossFit Level 1, BPJEPS, STAPS)</li>
                <li>une vraie culture de coaching et d'accompagnement</li>
              </ul>
            </div>

            {/* FAQ */}
            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">FAQ — Découvrir le CrossFit</h2>
              <div className="space-y-4">
                {faq.map((q) => (
                  <details key={q.name} className="group border border-border bg-card rounded-lg p-5 open:border-gold/50">
                    <summary className="cursor-pointer font-heading text-lg text-foreground flex justify-between items-center gap-4 list-none">
                      <span>{q.name}</span>
                      <ChevronRight className="w-5 h-5 text-gold shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-foreground/85 leading-relaxed font-body normal-case">{q.acceptedAnswer.text}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA final */}
            <section className="my-16">
              <div className="border border-gold/30 bg-gradient-to-br from-charcoal/60 to-charcoal/20 rounded-lg px-6 py-10 text-center">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Découvrez ce qu'est vraiment le CrossFit</h2>
                <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">
                  Une ambiance conviviale et accessible, deux séances d'essai gratuites, et un coach qui vous accompagne dès la première minute.
                </p>
                <CTAButtons />
              </div>
            </section>

            {/* Maillage interne */}
            <section className="my-12 border-t border-border pt-8">
              <h2 className="text-xl font-heading text-foreground mb-4">À lire aussi</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-body normal-case">
                <li><Link to="/crossfit-debutant" className="text-gold hover:underline">→ CrossFit débutant</Link></li>
                <li><Link to="/reprendre-le-sport" className="text-gold hover:underline">→ Reprendre le sport</Link></li>
                <li><Link to="/cours/wod" className="text-gold hover:underline">→ Le cours WOD CrossFit</Link></li>
                <li><Link to="/cours/hybrid-training" className="text-gold hover:underline">→ Hybrid Training (HYROX)</Link></li>
                <li><Link to="/blog/que-recherchez-vous-dans-le-crossfit" className="text-gold hover:underline">→ Que recherchez-vous dans le CrossFit ?</Link></li>
                <li><Link to="/lexique" className="text-gold hover:underline">→ Lexique CrossFit</Link></li>
              </ul>
            </section>

          </article>
                  <BlogStickyCTA />
        </main>
        <Footer />
      </div>
    </>
  );
}
